// The number printed on each wearable's case (its "field label", e.g. 002),
// from the API's one-call list `GET /v2/wearables/`.
//
// The case dashboard knows each wearable's full MAC. The Bluetooth update
// screen only knows its advertising name SIT-XXXX (macOS hides Bluetooth
// addresses), the last 4 hex digits of the MAC, so it matches on those and
// shows no label when two registered wearables share them. Labels themselves
// aren't unique by design; they're shown as they are.

const hexOf = (mac) =>
  String(mac || "")
    .replace(/[^0-9a-f]/gi, "")
    .toUpperCase();

/**
 * `/v2/wearables/` rows → { forMac(mac), forName("SIT-XXXX") }, each
 * returning the display label ("002") or null.
 */
export const wearableLabels = (rows) => {
  const byMac = new Map();
  const bySuffix = new Map(); // last 4 hex → label, or null if shared
  for (const row of rows || []) {
    const mac = hexOf(row?.ble_mac);
    if (mac.length !== 12) continue;
    const label = row.field_label_display || null;
    byMac.set(mac, label);
    const suffix = mac.slice(-4);
    bySuffix.set(suffix, bySuffix.has(suffix) ? null : label);
  }
  return {
    forMac: (mac) => byMac.get(hexOf(mac)) || null,
    forName: (name) => {
      const m = /^SIT(?:-|_DFU_)([0-9A-F]{4})$/i.exec(String(name || ""));
      return m ? bySuffix.get(m[1].toUpperCase()) || null : null;
    },
  };
};

export const NO_LABELS = wearableLabels([]);

// Labels in number order (9 before 10); names (hex) as plain text.
const naturally = (a, b) =>
  String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
const plainly = (a, b) => String(a).localeCompare(String(b));

/**
 * Sort order for wearables by field label: 001, 002, … 010, then the
 * unlabelled ones by name. Equal labels (two units marked 001) go by name.
 */
export const byFieldLabel = (a, b) => {
  if (a.label && b.label) {
    return naturally(a.label, b.label) || plainly(a.name, b.name);
  }
  if (a.label) return -1;
  if (b.label) return 1;
  return plainly(a.name, b.name);
};
